import { create } from "zustand";
import { combine } from "zustand/middleware";
import axios, { AxiosError, AxiosResponse } from "axios";
import { User } from "../models/User";
import { MobileUser, Role } from "/src/models";
import config from "/src/config";
import { parseJwt } from "/src/models/utils";
import { StorageKeys, getStorageItem, setStorageItem } from "/src/services/storages/StorageKeys";

const initialUseUserState = {
  user: {
    id: "",
    username: "",
    password: "",
    access_token: "",
    refresh_token: "",
    roles: [],
    redirection: false,
  } as User,
  fetching: {
    isLoading: false,
    isRefreshing: false,
    error: undefined as AxiosError<User> | undefined,
    response: undefined as AxiosResponse<User> | undefined,
    refreshRequest: undefined as Promise<User> | undefined,
  },
};

type UseUserState = typeof initialUseUserState;

const useUser = create(
  combine(
    {
      ...loadUseUserState(),
    },
    (set) => ({
      set,
    }),
  ),
);

function loadUseUserState(): UseUserState {
  try {
    const _userStorage = getStorageItem(StorageKeys.user);
    return JSON.parse(_userStorage ?? JSON.stringify(initialUseUserState));
  } catch (e) {
    return initialUseUserState;
  }
}

function saveUseUserState(useUserState: UseUserState) {
  try {
    setStorageItem(StorageKeys.user, JSON.stringify(useUserState));
  } catch (e) {
    console.error("failed to save auth user to storage");
  }
  return;
}

function checkLoginAccess(userRoles: Role[]) {
  if (
    ![Role.ROLE_ADMIN, Role.ROLE_ORG_ADMIN, Role.ROLE_USER].some((role) =>
      userRoles.some((userRole) => userRole === role),
    )
  ) {
    // window.location.href = '/person/list';
    // if ([Role.ROLE_MOBILE_USER].some(role => userRoles.some(userRole => userRole === role))) {
    //   throw new Error('This account is a mobile user, please login through Pannovation APP.');
    // }
    // throw new Error('No access to current platform');
  }
}

export async function login(
  userFormData: { username: string; password: string },
  cb?: { onError?: (error: AxiosError<User>) => any },
): Promise<User | void> {
  useUser.setState((state) => ({ fetching: { ...state.fetching, isLoading: true } }));
  try {
    const originUseUserState = useUser.getState();
    // 新版 API: POST /api/auth/login
    const res = await axios.post<User>(`${config.apiBase}/api/auth/login`, userFormData);
    const jwtUser = parseJwt(res.data.access_token!);
    const redirection = jwtUser.user?.id !== originUseUserState.user.id;
    const user = { ...res.data, ...jwtUser, id: jwtUser.user?.id ?? jwtUser.sub, redirection };

    // if mobile user portal
    if (window.location.href.indexOf("mobile") > -1) {
      if (!user.roles.some((v: any) => v === Role.ROLE_MOBILE_USER)) {
        const error = new Error();
        error.message = "Only Mobile user is allowed";
        throw error;
      } else {
        user.roles = [Role.ROLE_MOBILE_USER];
      }
    } else {
      if (
        !user.roles.some(
          (v: any) => v === Role.ROLE_ADMIN || v === Role.ROLE_USER || v === Role.ROLE_ORG_ADMIN,
        )
      ) {
        const error = new Error();
        error.message = "Mobile user is not allowed";
        throw error;
      }
    }

    if (user.roles.some((v: any) => v === Role.ROLE_MOBILE_USER)) {
      const mobileUser = await axios
        .get<MobileUser>(`${config.apiBase}/api/mobile-users/${user.id}`, {
          headers: {
            Authorization: `Bearer ${res.data.access_token}`,
          },
        })
        .then((res) => res.data);

      user.mobileUserId = mobileUser.id;
      user.mobileUser = mobileUser;
    }
    checkLoginAccess(user.roles);
    useUser.setState((state) => ({
      fetching: { ...state.fetching, isLoading: false },
      user: {
        ...state.user,
        ...user,
      },
    }));
    saveUseUserState(useUser.getState());
    return useUser.getState().user;
  } catch (e: any) {
    useUser.setState((state) => ({
      fetching: { ...state.fetching, isLoading: false },
    }));
    cb?.onError?.(e);
  }
}

export async function refresh(): Promise<User> {
  const originUseUserState = useUser.getState();

  // 如果没有 refresh_token，直接返回空用户
  if (!originUseUserState.user.refresh_token) {
    return originUseUserState.user;
  }

  useUser.setState((state) => ({ fetching: { ...state.fetching, isLoading: true } }));
  try {
    // 新版 API: POST /api/auth/refresh
    const res = await axios.post<User>(`${config.apiBase}/api/auth/refresh`, {
      refreshToken: originUseUserState.user.refresh_token,
    });
    const jwtUser = parseJwt(res.data.access_token!);
    const user = { ...res.data, ...jwtUser, id: jwtUser.user?.id ?? jwtUser.sub };
    checkLoginAccess(user.roles);
    useUser.setState((state) => ({
      fetching: { ...state.fetching, isLoading: false },
      user: {
        ...state.user,
        ...user,
      },
    }));
    saveUseUserState(useUser.getState());
    return useUser.getState().user;
  } catch (e) {
    useUser.setState((state: any) => ({
      fetching: { ...state.fetching, isLoading: false, error: e },
      user: {
        ...state.user,
        access_token: "",
        refresh_token: "",
      },
    }));
    throw e;
  } finally {
    useUser.setState((state) => ({
      fetching: { ...state.fetching, refreshRequest: undefined },
    }));
  }
}

export function getRefreshRequest(): Promise<User> {
  if (!useUser.getState().fetching.refreshRequest?.then) {
    useUser.setState((state) => ({
      fetching: { ...state.fetching, refreshRequest: refresh() },
    }));
  }
  return useUser.getState().fetching.refreshRequest!;
}

export function logout() {
  useUser.setState((state) => ({
    user: {
      ...state.user,
      access_token: "",
      refresh_token: "",
    },
  }));
  saveUseUserState(useUser.getState());
}

export function useAccessible(roles: Role[]) {
  const userRoles = useUser((state) => state.user.roles);
  return roles.some((role) => userRoles.some((userRole) => userRole === role));
}

export default useUser;
